<template>
  <div class="applicant-random-dialog">
    <el-dialog
      custom-class="with-title"
      title="ランダム抽選"
      :visible.sync="visible"
      :close-on-click-modal="!loading"
      :show-close="!loading"
    >
      <div
        v-loading="loading"
        :element-loading-text="isSubmit ? '応募者をランダム抽選しています、しばらくお待ちください。' : ''"
      >
        <div class="dialog-content">
          <div class="message message-info mb-x3">
            現在の検索条件に該当する応募者{{
              formatNumber(applicantCount)
            }}件から、指定した数ランダム抽選します。<br />すでに設定されている応募者は除外されます。
          </div>
          <!-- 種別-->
          <div class="input-container">
            <div class="label">
              種別
              <el-tooltip placement="top">
                <div slot="content">ランダム抽選の結果、付与したいフラグの種別を選択してください。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <el-radio border v-model="randomType" label="winner" tabindex="1">当選者</el-radio>
            <el-radio border v-model="randomType" label="candidate" tabindex="2">候補者</el-radio>
          </div>
          <!-- 抽選数-->
          <div class="input-container">
            <div class="label">抽選数</div>
            <div class="input">
              <el-input-number
                controls-position="right"
                :min="1"
                :max="maxRandomCount"
                v-model="randomCount"
                @change="onChangeRandomCount"
              ></el-input-number
              ><span class="applicant-random-remain ml-x2" v-if="randomType === 'winner'"
                >残り当選数: {{ formatNumber(remainWinnerCount - randomCount) }}</span
              >
            </div>
          </div>
          <div class="input-container">
            <div class="caption">除外設定</div>
          </div>
          <div class="input-container">
            <el-switch v-model="isExcludeWasWinner" active-text="過去に当選者にした応募者を除外する"></el-switch>
          </div>
          <div class="input-container">
            <el-switch
              v-model="isExcludeWinnerCampaigns"
              active-text="過去に開催したキャンペーンの当選者を除外する"
            ></el-switch>
          </div>
        </div>
        <div class="dialog-footer">
          <div class="caption">
            抽選対象: {{ entryCounts }}件
            <el-tooltip placement="top" content="除外設定がONの場合、対象の応募者は抽選から除外されます。">
              <i class="mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <el-button type="primary" @click="submit()" :disabled="disabled" class="filtering-intensity-container"
            ><i class="mdi mdi-dice-multiple"></i>ランダム抽選を実行</el-button
          >
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-loading-spinner) {
  margin-top: math.div(-$spacing-unit * 12, 2) - $spacing-unit * 2;

  .circular {
    width: $spacing-unit * 12;
    height: $spacing-unit * 12;
  }

  .el-loading-text {
    margin-top: $spacing-unit * 2;
    font-weight: bold;
    color: $text-color;
  }
}

.filtering-intensity-container {
  margin-top: $spacing-unit;
}

.applicant-random-remain {
  color: $sub-text-color;
}
</style>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.input-container {
  width: 100%;
  margin-bottom: $spacing-unit * 2;

  .label {
    margin-bottom: $spacing-unit;
  }

  .el-input-number {
    width: $spacing-unit * 18;
  }
}

.dialog-footer {
  text-align: right;
}

.winner-option {
  width: $spacing-unit * 45;
}

:deep(.el-tag) {
  max-width: $spacing-unit * 40;
}

:deep(.el-select__tags-text) {
  display: inline-block;
  max-width: $spacing-unit * 35;
  overflow: hidden;
  text-overflow: ellipsis;
  color: $text-color;
  white-space: nowrap;
}

:deep(.el-select .el-tag__close.el-icon-close) {
  top: -$spacing-unit;
}

:deep(.el-select-dropdown__item span) {
  display: inline-block;
  width: 99%;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.el-select__input) {
  margin-left: 10px;
}
</style>
<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { DeepReadonly, defineComponent } from 'vue'

import api from '@/client/core/api'
import { formatNumber } from '@/client/filters/filters'
import { useTikTokApplicantStore } from '@/client/store/useTikTokApplicantStore'
import { TEntryCount } from '@/typings/api/tiktok_applicants.type'

export default defineComponent({
  data() {
    return {
      randomCount: 1,
      visible: false,
      loading: false,
      isExcludeWasWinner: true,
      isExcludeWinnerCampaigns: false,
      isSubmit: false,
      randomType: 'winner'
    }
  },
  computed: {
    ...mapState(useTikTokApplicantStore, {
      applicantCount: state => state.total,
      campaignId: state => state.campaign?.id,
      campaignAccountId: state => state.campaign?.account?.id,
      prizeId: state => state.campaignPrize?.id,
      applicantParams: state => state.params,
      remainWinnerCount: state => {
        if (!state.campaignPrize) {
          return 0
        }
        return state.campaignPrize.maxWinnerCount - state.campaignPrize.winnerCount
      },
      entryCounts: state => state.entryCounts
    }),
    maxRandomCount() {
      if (this.randomType === 'candidate') {
        return this.applicantCount < 999 ? this.applicantCount : 999
      }

      return this.applicantCount < this.remainWinnerCount ? this.applicantCount : this.remainWinnerCount
    },
    url() {
      return this.randomType === 'candidate'
        ? '/tiktok_applicants/random_candidates'
        : '/tiktok_applicants/random_winners'
    },
    disabled() {
      return this.randomType === 'winner' && this.remainWinnerCount <= 0
    }
  },
  watch: {
    async randomType() {
      await this.refreshEntryCount()

      if (this.randomCount <= this.maxRandomCount) {
        return
      }

      this.randomCount = this.maxRandomCount
    },
    async isExcludeWinnerCampaigns() {
      await this.refreshEntryCount()
    },
    async isExcludeWasWinner() {
      await this.refreshEntryCount()
    }
  },
  methods: {
    formatNumber,

    ...mapActions(useTikTokApplicantStore, ['fetchApplicants', 'fetchCampaignPrize', 'fetchEntryCount']),
    /**
     * ダイアログリセット
     */
    reset() {
      this.randomCount = 1
      this.loading = false
      this.isSubmit = false
      this.randomType = 'winner'
    },

    /**
     * フォームサブミット
     */
    async submit() {
      if (this.isSubmit) {
        return
      }
      const confirm = window.confirm('この操作は取り消しできません、実行してもよろしいですか?')
      if (!confirm) {
        return
      }
      this.isSubmit = true
      this.loading = true

      const applicantParamsClone = { ...this.applicantParams }

      // 過去に当選したことのある応募者を除外する
      if (this.isExcludeWasWinner) {
        applicantParamsClone.wasWinner = 0
      }

      let params = {
        campaignId: this.campaignId,
        prizeId: this.prizeId,
        randomCount: this.randomCount,
        isExcludeWinnerCampaigns: this.isExcludeWinnerCampaigns
      }

      params = { ...applicantParamsClone, ...params }

      const result = await api.post(this.url, params)
      this.loading = false
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)
      // 残り当選者数が足りない場合はエラーを表示
      if (result && result.error && result.error.type === 'MAX_WINNER_COUNT_OVER') {
        this.$notify({
          title: '残り当選者数が足りません。',
          message: '当選者数を変更して、再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        this.fetchCampaignPrize()
        this.fetchApplicants()
        return
      }

      // 失敗した場合はエラーを表示
      if (!result || isNaN(result.data)) {
        this.$notify({
          title: 'ランダム抽選に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'ランダム抽選が完了しました。',
        message: `${result.data}人の応募者を当選者にしました。`,
        customClass: 'success',
        duration: 5000
      })

      this.fetchCampaignPrize()
      this.fetchApplicants()
      this.close()
    },

    /**
     * 応募者数変更イベント
     * @param {number} value バリュー
     */
    onChangeRandomCount(value: number) {
      if (!value) {
        setTimeout(() => {
          this.randomCount = 1
        }, 100)
      }
    },

    async open() {
      this.visible = true
      this.reset()
      await this.refreshEntryCount()
    },
    close() {
      this.visible = false
    },

    async refreshEntryCount() {
      this.loading = true

      const applicantParams = { ...this.applicantParams }

      // 過去に当選したことのある応募者を除外する
      if (this.isExcludeWasWinner) {
        applicantParams.wasWinner = 0
      }

      let params: DeepReadonly<TEntryCount> = {
        campaignId: this.campaignId,
        prizeId: this.prizeId,
        isExcludeWinnerCampaigns: this.isExcludeWinnerCampaigns,
        randomType: this.randomType
      }

      if (this.randomType === 'candidate') {
        params = { ...params, applicantParams }
      } else {
        params = {
          ...params,
          ...applicantParams
        }
      }

      await this.fetchEntryCount(params)

      this.loading = false
    }
  }
})
</script>
